import { CheckIcon, ClipboardDocumentIcon } from "@heroicons/react/16/solid";
import { ReactElement } from "react";

import { useCopyToClipboard } from "../hooks/use-copy-to-clipboard.ts";
import { Button } from "./catalyst/Button.tsx";

export interface CopyButtonProps {
  className?: string;
  content: string;
  errorMessage?: string;
  successMessage?: string;
  title?: string;
}

/**
 * A reusable copy button component that handles copying text to clipboard
 * with visual feedback (checkmark when copied, clipboard icon otherwise).
 *
 * @param content - The text content to copy to clipboard
 * @param title - Custom title for the button tooltip (defaults to "Copy")
 * @param className - Custom className for the button
 * @param successMessage - Custom success message (defaults to "Copied to clipboard")
 * @param errorMessage - Custom error message (defaults to "Failed to copy to clipboard")
 *
 * @example
 * ```tsx
 * <CopyButton
 *   content="Hello World"
 *   title="Copy text"
 *   className="absolute right-2 top-2"
 * />
 * ```
 */
export const CopyButton = ({
  className,
  content,
  errorMessage = "Failed to copy to clipboard",
  successMessage = "Copied to clipboard",
  title = "Copy",
}: CopyButtonProps): ReactElement => {
  const { copied, copyToClipboard } = useCopyToClipboard({
    errorMessage,
    successMessage,
  });

  const handleCopy = (): void => {
    void copyToClipboard(content);
  };

  return (
    <Button
      className={className}
      onClick={handleCopy}
      plain
      size="xs"
      title={title}
    >
      {copied ? (
        <CheckIcon className="h-4 w-4 text-green-500" data-slot="icon" />
      ) : (
        <ClipboardDocumentIcon
          className="h-4 w-4 text-gray-400 hover:text-white"
          data-slot="icon"
        />
      )}
    </Button>
  );
};
